import * as React from 'react';
import { connect } from 'react-redux';
import { log_error } from '../../shared/action/shared_action';
import { Button, Dimmer, Grid, Loader } from 'semantic-ui-react';
import * as AppliedModifiersConstants from '../constant/applied_modifiers_constants';
import { AppliedModifierFormData, UI_CONSTANTS, transformFormDataToBackendDto } from '../constant/applied_modifiers_constants';
import { useAppliedModifiersForm } from '../hooks/useAppliedModifiersForm';
import { useAppliedModifiersData } from '../hooks/useAppliedModifiersData';
import { useAppliedModifiersValidation } from '../hooks/useAppliedModifiersValidation';
import { useAppliedModifiersUtils } from '../hooks/useAppliedModifiersUtils';
import { validate_applied_modifier } from '../action/applied_modifiers_action';
import { BasicInfoSection } from './AppliedModifiersForm/BasicInfoSection';
import { ApplySection } from './AppliedModifiersForm/ApplySection';
import { WhenSection } from './AppliedModifiersForm/WhenSection';

export interface AppliedModifiersAddEditComponentProps {
    user_login_details?: any;
    onClose?: () => void;
    onSave?: (data: AppliedModifierFormData) => void;
    editData?: AppliedModifierFormData & { claim_processing_rule_id?: number };
    updateHandlerCallback?: (formData: AppliedModifierFormData) => void;
    'data-testid'?: string;
    modifierList?: any[];
    toChargeByList?: any[];
    primarySpecialtyList?: any[];
    payerList?: any[];
    insuranceClassList?: any[];
}

export const AppliedModifiersAddEditComponent: React.FC<AppliedModifiersAddEditComponentProps> = ({
    user_login_details,
    onClose,
    onSave,
    editData,
    updateHandlerCallback,
    'data-testid': dataTestId,
    modifierList,
    toChargeByList,
    primarySpecialtyList,
    payerList,
    insuranceClassList
}) => {
    const isEdit = !!editData;
    
    // Filter toChargeByList based on mode and selected items
    const filteredToChargeByList = React.useMemo(() => {
        if (!toChargeByList || toChargeByList.length === 0) {
            return toChargeByList;
        }

        if (!isEdit) {
            // Add mode: Remove ALL inactive procedure codes from tab 2
            return toChargeByList.map((tab: any) => {
                // For tab 2 (Procedure Code), filter out inactive items
                if (tab.key === AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab2.label) {
                    return {
                        ...tab,
                        items: tab.items.filter((item: any) =>
                            !item.label.endsWith(AppliedModifiersConstants.UI_CONSTANTS.DATA_PROCESSING.INACTIVE_SUFFIX)
                        )
                    };
                }
                // For tab 1 (Revenue Center), keep all items
                return tab;
            });
        } else {
            // Edit mode: Keep inactive procedure codes ONLY if they are selected
            const selectedProcCodeIds: number[] = [];
            
            if (editData && editData.to_charge_by && Array.isArray(editData.to_charge_by)) {
                editData.to_charge_by.forEach((item: any) => {
                    // Only collect procedure code IDs (tabId 1 = procedure codes based on ChargeByTabId enum)
                    if (item.tabId === AppliedModifiersConstants.ChargeByTabId.ProcedureCode) {
                        selectedProcCodeIds.push(item.value);
                    }
                });
            }
            
            return toChargeByList.map((tab: any) => {
                // For tab 2 (Procedure Code), filter out inactive items that are not selected
                if (tab.key === AppliedModifiersConstants.revenue_centers_and_proc_codes_tab.tab2.label) {
                    return {
                        ...tab,
                        items: tab.items.filter((item: any) => {
                            const isInactive = item.label.endsWith(AppliedModifiersConstants.UI_CONSTANTS.DATA_PROCESSING.INACTIVE_SUFFIX);
                            
                            if (!isInactive) {
                                // Active procedure codes: always show
                                return true;
                            } else {
                                // Inactive procedure codes: only show if selected
                                return selectedProcCodeIds.indexOf(item.value) !== -1;
                            }
                        })
                    };
                }
                // For tab 1 (Revenue Center), keep all items
                return tab;
            });
        }
    }, [toChargeByList, isEdit, editData]);
    
    // Custom hooks for state management
    const { formData, updateField, isFormValid, isSubmitted, setIsSubmitted } = useAppliedModifiersForm(editData);
    const { loading, dropdownData, panelData, error } = useAppliedModifiersData(
        user_login_details, 
        isEdit,
        modifierList,
        filteredToChargeByList,
        primarySpecialtyList,
        payerList,
        insuranceClassList
    );
    const { markFieldForRequiredValidation, markFieldForValidation } = useAppliedModifiersValidation(formData, isSubmitted);
    const { 
        getDisplayFormatDate, 
        convertInDateFormat, 
        getMemoizedToChargeByValues
    } = useAppliedModifiersUtils(formData);

    // Local state for UI-specific concerns
    const [isSaveInProgress, setIsSaveInProgress] = React.useState(false);
    const [hasDateOfServiceRangeError, setHasDateOfServiceRangeError] = React.useState(false);
    const [insuranceClassTabDisabled, setInsuranceClassTabDisabled] = React.useState(false);
    const [excludeInsuranceClassTabDisabled, setExcludeInsuranceClassTabDisabled] = React.useState(false);
    const [validationMsg, setValidationMsg] = React.useState<Record<string, string>>({});
    

    // Initialize tab disabled states based on existing data
    React.useEffect(() => {
        if (isEdit && editData) {
            const hasInsuranceClassSelections = editData.insurance_class && editData.insurance_class.length > 0;
            const hasExcludedInsuranceClassSelections = editData.excluded_insurance_class && editData.excluded_insurance_class.length > 0;
            
            setExcludeInsuranceClassTabDisabled(hasInsuranceClassSelections);
            setInsuranceClassTabDisabled(hasExcludedInsuranceClassSelections);
        }
    }, [isEdit, editData]);


    // Handle tab disabled states when selections change
    React.useEffect(() => {
        const hasInsuranceClassSelections = formData.insurance_class && formData.insurance_class.length > 0;
        const hasExcludedInsuranceClassSelections = formData.excluded_insurance_class && formData.excluded_insurance_class.length > 0;
        
        setExcludeInsuranceClassTabDisabled(hasInsuranceClassSelections);
        setInsuranceClassTabDisabled(hasExcludedInsuranceClassSelections);
    }, [formData]);

    // Helper function to get validation message
    const getValidationMessage = React.useCallback((serviceMsg: string | null | undefined, validationObjectMsg: string) => {
        if (serviceMsg != null && serviceMsg !== undefined) {
            return `${serviceMsg} ${validationObjectMsg}`;
        }
        return validationObjectMsg;
    }, []);

    const serviceValidate = React.useCallback(async (appliedModifierObj: any) => {
        setIsSaveInProgress(true);
        
        try {
            const token = user_login_details && 
                         user_login_details.user_data && 
                         user_login_details.user_data.data && 
                         user_login_details.user_data.data.token_details && 
                         user_login_details.user_data.data.token_details.access_token;
            if (!token) {
                log_error('No access token available');
                setIsSaveInProgress(false);
                return;
            }

            const response = await validate_applied_modifier(token, appliedModifierObj);
            
            if (response && response.data && response.data.data) {
                let hasServiceValidation = false;
                const validationMsgObj: Record<string, string> = {};
                
                if (response.data.data.validation && response.data.data.validation.length > 0) {
                    hasServiceValidation = true;
                    response.data.data.validation.forEach((validation: any) => {
                        const propertyName = validation.propertyName || '';
                        validationMsgObj[propertyName] = getValidationMessage(
                            validationMsgObj[propertyName],
                            validation.message
                        );
                    });
                }
                
                if (hasServiceValidation) {
                    setValidationMsg(validationMsgObj);
                    setIsSaveInProgress(false);
                } else {
                    setValidationMsg({});
                    // Validation passed, proceed with save
                    if (onSave) {
                        onSave(formData);
                    }
                }
            } else {
                setValidationMsg({});
                setIsSaveInProgress(false);
            }
        } catch (error) {
            log_error(error);
            setValidationMsg({});
            setIsSaveInProgress(false);
        }
    }, [formData, user_login_details, getValidationMessage, onSave]);

    // Event handlers
    const handleSave = React.useCallback(() => {
        setIsSubmitted(true);
        // Check form validity AND date picker's internal error state
        // Similar to claims_scrubbing_procedure_replacement_add_edit_new_component.tsx line 256
        if (isFormValid() && !hasDateOfServiceRangeError) {
            // Transform form data to backend DTO format
            const claimProcessingRuleId = (editData && editData.claim_processing_rule_id) ? editData.claim_processing_rule_id : 0;
            const backendDto = transformFormDataToBackendDto(formData, claimProcessingRuleId);
            
            // Call service validation first
            serviceValidate(backendDto);
        } else {
            setIsSaveInProgress(false);
        }
    }, [formData, isFormValid, onSave, setIsSubmitted, hasDateOfServiceRangeError, serviceValidate, editData]);

    const handleClose = React.useCallback(() => {
        setIsSaveInProgress(false);
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    const handleDateOfServiceRangeHasError = React.useCallback((hasError: boolean) => {
        setHasDateOfServiceRangeError(hasError);
    }, []);

    const removeServiceValidation = React.useCallback((fieldName: string) => {
        setValidationMsg(prev => {
            if (prev && prev[fieldName]) {
                const updated = { ...prev };
                updated[fieldName] = '';
                return updated;
            }
            return prev;
        });
    }, []);


    const isSaveButtonDisabled = React.useCallback(() => {
        return isSaveInProgress;
    }, [isSaveInProgress]);

    // Notify parent component of updates
    React.useEffect(() => {
        if (updateHandlerCallback) {
            updateHandlerCallback(formData);
        }
    }, [formData]); // Remove updateHandlerCallback from dependencies to prevent infinite loops

    // Render method
    return (
        <div data-testid={dataTestId}>
            <Dimmer active={loading}>
                    <Loader size='massive'>{UI_CONSTANTS.LABELS.LOADING_TEXT}</Loader>
                </Dimmer>
            
            {error && (
                <div className="error-message">
                    Error loading data: {error}
                </div>
            )}
            
                <div className={UI_CONSTANTS.CSS_CLASSES.BP3_DIALOG_SAVE_BUTTON}>
                    <Button
                        id={AppliedModifiersConstants.save_button.id}
                        className={UI_CONSTANTS.CSS_CLASSES.PRIMARY_BUTTON}
                        type='button'
                    disabled={isSaveButtonDisabled()}
                    onClick={handleSave}
                    data-testid={AppliedModifiersConstants.save_button.id}>
                    {AppliedModifiersConstants.save_button.name}
                </Button>
                </div>

                <Grid>
                <BasicInfoSection 
                    formData={formData}
                    onFieldChange={updateField}
                    markFieldForRequiredValidation={markFieldForRequiredValidation}
                    markFieldForValidation={markFieldForValidation}
                    validationMsg={validationMsg}
                    onClearValidation={removeServiceValidation}
                />
                
                <ApplySection 
                    formData={formData}
                    onFieldChange={updateField}
                    markFieldForRequiredValidation={markFieldForRequiredValidation}
                    markFieldForValidation={markFieldForValidation}
                    dropdownData={dropdownData}
                    isSaveButtonDisabled={isSaveButtonDisabled()}
                    getMemoizedToChargeByValues={getMemoizedToChargeByValues}
                />
                
                <WhenSection 
                    formData={formData}
                    onFieldChange={updateField}
                    markFieldForRequiredValidation={markFieldForRequiredValidation}
                    markFieldForValidation={markFieldForValidation}
                    dropdownData={dropdownData}
                    panelData={panelData}
                    isSaveButtonDisabled={isSaveButtonDisabled()}
                    insuranceClassTabDisabled={insuranceClassTabDisabled}
                    excludeInsuranceClassTabDisabled={excludeInsuranceClassTabDisabled}
                    hasDateOfServiceRangeError={hasDateOfServiceRangeError}
                    onDateOfServiceRangeHasError={handleDateOfServiceRangeHasError}
                    getDisplayFormatDate={getDisplayFormatDate}
                    convertInDateFormat={convertInDateFormat}
                    isSubmitted={isSubmitted}
                />
                </Grid>
                
            </div>
        );
};


const mapStateToProps = (state: any) => {
    return {
        user_login_details: state.user_login_details,
    };
};

export default connect(mapStateToProps)(AppliedModifiersAddEditComponent);
